import type { LanguageEntry, RightToWorkEntry } from "@/lib/taxonomy";

type UnknownRecord = Record<string, unknown>;

export type CreateProfileAvailability = {
  season_label: string;
  available_from: string;
  available_to: string;
  dates_flexible: boolean;
  flexible_earliest_start: string | null;
  flexible_latest_end: string | null;
  positions: string[];
  preferred_locations: string[];
};

export type CreateProfilePayload = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  dob: string | null;
  gender: string | null;
  birth_date: string | null;
  nationality: string | null;
  right_to_work: RightToWorkEntry[];
  current_location: string | null;
  photo_url: string | null;
  bio: string | null;
  languages: LanguageEntry[] | null;
  qualifications: string[];
  experience_level: string | null;
  hide_email: boolean;
  hide_phone: boolean;
  last_active: string;
  referral_code: string;
  availabilities: CreateProfileAvailability[];
};

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function optionalText(value: unknown): string | null {
  const valueText = text(value);
  return valueText || null;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is string => typeof entry === "string")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function normaliseRightToWork(value: unknown): RightToWorkEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is RightToWorkEntry => {
    if (!entry || typeof entry !== "object") return false;
    const record = entry as UnknownRecord;
    return text(record.region).length > 0 && text(record.status).length > 0;
  });
}

function normaliseLanguages(value: unknown): LanguageEntry[] | null {
  if (!Array.isArray(value)) return null;
  const languages = value.filter((entry): entry is LanguageEntry => {
    if (!entry || typeof entry !== "object") return false;
    const record = entry as UnknownRecord;
    return text(record.language).length > 0;
  });
  return languages.length > 0 ? languages : null;
}

function normaliseAvailability(
  value: unknown,
  fallbackPositions: string[],
  fallbackLocations: string[],
): CreateProfileAvailability[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const record = entry as UnknownRecord;
    const positions = stringArray(record.positions);
    const effectivePositions =
      positions.length > 0 ? positions : fallbackPositions;
    const preferredLocations = stringArray(record.preferred_locations);
    const effectiveLocations =
      preferredLocations.length > 0
        ? preferredLocations
        : fallbackLocations;
    const seasonLabel = text(record.season_label);
    const availableFrom = text(record.available_from);
    const availableTo = text(record.available_to);

    if (
      !seasonLabel ||
      !availableFrom ||
      !availableTo ||
      effectivePositions.length === 0
    ) {
      return [];
    }

    return [
      {
        season_label: seasonLabel,
        available_from: availableFrom,
        available_to: availableTo,
        dates_flexible:
          typeof record.dates_flexible === "boolean"
            ? record.dates_flexible
            : true,
        flexible_earliest_start: optionalText(
          record.flexible_earliest_start,
        ),
        flexible_latest_end: optionalText(record.flexible_latest_end),
        positions: effectivePositions,
        preferred_locations: effectiveLocations,
      },
    ];
  });
}

export function buildCandidateReferralCode(
  firstName: string,
  lastName: string,
  suffix = Math.random().toString(36).slice(2, 6),
): string {
  const first = text(firstName).toLowerCase() || "member";
  const lastInitial = text(lastName).charAt(0).toLowerCase() || "p";
  return `${first}-${lastInitial}-${suffix}`;
}

export function buildCreateProfilePayload(input: {
  userId: string;
  authEmail?: string | null;
  signupData: UnknownRecord;
  now?: string;
  referralCode?: string;
}): CreateProfilePayload {
  const data = input.signupData;
  const firstName = text(data.firstName ?? data.first_name);
  const lastName = text(data.lastName ?? data.last_name);
  const birthDate = optionalText(
    data.birthDate ?? data.birth_date ?? data.dob,
  );
  const fallbackPositions = stringArray(data.positions);
  const fallbackLocations = stringArray(data.locations);

  return {
    user_id: input.userId,
    first_name: firstName,
    last_name: lastName,
    email: text(data.email) || text(input.authEmail),
    phone: optionalText(data.phone),
    dob: birthDate,
    gender: optionalText(data.gender),
    birth_date: birthDate,
    nationality: optionalText(data.nationality),
    right_to_work: normaliseRightToWork(
      data.rightToWork ?? data.right_to_work,
    ),
    current_location: optionalText(
      data.currentLocation ?? data.current_location,
    ),
    photo_url: optionalText(data.photoUrl ?? data.photo_url),
    bio: optionalText(data.bio),
    languages: normaliseLanguages(data.languages),
    qualifications: stringArray(data.qualifications),
    experience_level: optionalText(
      data.previousSeasons ?? data.experience_level,
    ),
    hide_email:
      typeof data.hideEmail === "boolean"
        ? data.hideEmail
        : typeof data.hide_email === "boolean"
          ? data.hide_email
          : true,
    hide_phone:
      typeof data.hidePhone === "boolean"
        ? data.hidePhone
        : typeof data.hide_phone === "boolean"
          ? data.hide_phone
          : true,
    last_active: input.now ?? new Date().toISOString(),
    referral_code:
      input.referralCode ??
      buildCandidateReferralCode(firstName, lastName),
    availabilities: normaliseAvailability(
      data.availabilities,
      fallbackPositions,
      fallbackLocations,
    ),
  };
}
